import { ActivitySpinnerWidget } from '@tableau/widgets';
import React from 'react';
import { defaultMemoize } from 'reselect';
import tabbable from 'tabbable';
import _ from 'underscore';

import { Telemetry } from '~commonInfra/services/Telemetry';
import {
  ActionMenuItemClickedChildFunction,
  ActionMenuItemClickedTelemetryContext,
  CardPositionContext,
  ContentClickedChildFunction,
  ContentClickedTelemetryContext,
  ContentClickedTelemetryContextValueType,
  ContentInfoContext,
  ExplorePageDisplayModeContext,
  FavoriteToggleClickedChildFunction,
  FavoriteToggleClickedTelemetryContext,
  RelatedContentInfoContext,
  SearchTermDataContext
} from '~commonInfra/telemetryEvents/contexts/TelemetryContexts';
import { CreateExplorerEvent } from '~commonInfra/telemetryEvents/TelemetryEventCreator';
import {
  AdvancedFilterPanelToggleEvent,
  ShowExplorerEvent,
  TelemetryEvents,
  VPCFeatureUsageEvent
} from '~commonInfra/telemetryEvents/TelemetryEvents';
import {
  ContentLocation,
  ExploreDisplayMode,
  FavoriteToggleType
} from '~commonInfra/telemetryEvents/types/ContentTelemetryTypes';
import { generateTelemetryEventHandler } from '~commonInfra/telemetryEvents/util/ContentTelemetry';
import { Location } from '~commonInfra/utils/Location';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { CatalogProviders } from '~src/catalog/CatalogProviders';
import { Messages } from '~src/codegen/Localize';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { ScreenReaderService } from '~src/commonInfra/services/ScreenReaderService';
import { imgPath } from '~src/commonInfra/utils/ImgUrl';
import { IOrderClause } from '~src/spacesPlatform/serverApi/types/IOrderClause';
import { clamp } from '~src/spacesPlatform/utils/MathUtils';
import { ConnectedServerUsersAdvancedFilterPanel } from '~src/userManagement/users/components/explorer/ServerUsersAdvancedFilterPanel';
import { SiteUsersAdvancedFilterPanel } from '~src/userManagement/users/components/explorer/SiteUsersAdvancedFilterPanel';

import { DisplayMode } from '../../../sessionManagement/serverApi/types/ISessionInfo';
import { getSessionManagementServiceInstance } from '../../../sessionManagement/SessionManagementService';
import { ServerSettingsUnauthenticated } from '../../../settings/services/ServerSettingsUnauthenticated';
import { getWorkbookUrl } from '../../../workbook/components/WorkbookSpace';
import { ExplorerEmptyState, ExplorerEmptyStateProps } from '../../components/Explorer/widgets/ExplorerEmpyState';
import { getExplorableItemDefinition } from '../../contentDefinitionInterfaces/ContentDefinitionsLists';
import { ExplorableItemType } from '../../models/ContentType';
import { IContentInfo } from '../../models/IContentInfo';
import { IExplorableItemTypeInfo } from '../../models/resourceInfoParts';
import { ContentExplorerToolbarState, FilterMap } from '../../state/ContentExplorerState';
import {
  contentTypeToWorkbookSubspaceName,
  isAllServerUsersSpace,
  isGroupSetGroupsSubspace,
  isGroupSpace,
  isProjectSpace,
  isSiteSpace,
  isSiteUserContentSubspace,
  isSiteUsersSpace,
  isSomeKindaSearchSpace,
  IsWorkbookDatasourceSubspaceTab,
  SpaceInfo,
  WorkbookSubspaceName
} from '../../state/SpaceInfo';
import { ItemsSet } from '../../utils/ItemsSet';
import { ConnectedCardView } from '../card/CardView';
import { focusFilterSearchField } from '../filterPanel/SearchField';
import { spaceHeaderID } from '../header/SpaceHeaderWithTotalItemsCount';
import { ConnectedListView } from '../ListView/ConnectedListView';
import { SpacesSiteUrls } from '../spaceComponents/SpacesSiteUrls';
import { activeSubSpaceHeaderId } from '../spaceComponents/TabbedSpace';
import { AdvancedFilterPanel, AdvancedFilterPanelProps } from './AdvancedFilterPanel';
import { ConnectedExplorerToolbar } from './ConnectedExplorerToolbar';
import { DatasourceViewTypeInfo } from './DatasourceViewTypeDropdown';
import { ExplorerStyles } from './ExplorerStyles';
import { ExplorerTestIds } from './ExplorerTestIds';
import { ContentSpecificControls, ExplorerToolbar } from './ExplorerToolbar';
import { FilterPanelResizer, FilterPanelResizeState } from './FilterPanelResizer';

const FILTER_ICON_LARGE_PATH = 'spaces_filter_large.svg';
export const CONVERT_TO_MILLISECONDS = 1000;

export interface ExplorerStateProps {
  readonly contentTypeFilterValues: ReadonlyArray<ExplorableItemType>;
  readonly mixedContentFilterMap: FilterMap;
  readonly items: ReadonlyArray<IExplorableItemTypeInfo>;
  readonly isBusyFetchingItems: boolean;
  readonly selectedItems: ItemsSet<IExplorableItemTypeInfo>;
  readonly isSelectionBusy: boolean;
  readonly contentSort: ReadonlyArray<IOrderClause>;
  readonly explorerViewMode: DisplayMode;
  readonly showFilterPanel: boolean;
  readonly spaceInfo: SpaceInfo;
  readonly urls: SpacesSiteUrls;
  readonly catalogingOnSiteEnabled: boolean;
  readonly contentNextPageToken: string;
  readonly moreItemsForTokenRequest: boolean;
}

export interface ExplorerDispatchProps {
  readonly selectItem: (item: IContentInfo) => void;
  readonly deselectItem: (item: IContentInfo) => void;
  readonly clearAllSelections: () => void;
  readonly selectAll: () => void;
  readonly filterSelected: (selectedItems: ArrayLike<ExplorableItemType>) => void;
  readonly changeExplorerViewMode: (viewMode: DisplayMode, saveToServer: boolean) => void;
  readonly updateFilterPanelVisibility: (showFilterPanel: boolean) => void;
  readonly contentSortSelected: (order: ReadonlyArray<IOrderClause>) => void;
  readonly requestSpaceContentsMissingSlice: (start: number, end: number) => void;
  readonly requestSpaceContentNextPageWithToken: (nextPageToken: string) => void;
  readonly addFieldToSortOrder: (field: string) => void;
  readonly clearMixedContentFilters: () => void;
  readonly setToolbarState: (state: ContentExplorerToolbarState) => void;
}

export type ExplorerToolbarVisibility = 'enabled' | 'disabledButtonsOnRight' | 'hidden';

export interface PropsForExplorerToolbar {
  readonly supportFilterPanel: boolean;
  readonly hideSelection?: boolean;
  readonly toolbarContentFilterControl?: JSX.Element;
  readonly toolbarContentSpecificControls?: ContentSpecificControls;
  readonly toolbarVisibility: ExplorerToolbarVisibility;
  readonly hideContentSpecificControlsInEmptyState?: boolean;
}
export interface ExplorerOwnProps {
  readonly supportedDisplayModes: ReadonlyArray<DisplayMode>;
  readonly emptyStateProps?: ExplorerEmptyStateProps;
  readonly hideSelectionAndActionMenu?: boolean;
  readonly toolbarProps: PropsForExplorerToolbar;
  readonly reorderItemAction?: (fromIndex: number, toIndex: number, spaceInfo: SpaceInfo) => void;
  readonly allowDragAndDrop?: boolean;
  readonly disableSort?: boolean;
  readonly reorderAccessibilityInstructions?: string;

  // This does NOT disable the toolbar, just switches to use the one not connected to app state.
  // The connected toolbar is used in Compact Header scenarios, outside tabbed spaces.
  //
  // We should be able to remove this prop and its use when we sort out redux issues.
  readonly disableConnectedToolbar?: boolean;

  readonly belowToolbarContent?: React.ReactNode;
}

export const vpcUsageTelemetryExplorerDisplayModeFeatureName = 'ExplorerDisplayMode';

export type ExplorerProps = ExplorerStateProps & ExplorerDispatchProps & ExplorerOwnProps;
type ExplorerState = FilterPanelResizeState;
export class Explorer extends React.PureComponent<ExplorerProps, ExplorerState> {

  public state: ExplorerState = { filterPanelWidth: ExplorerStyles.DefaultAdvancedFilterPanelWidth, filterPanelCurrentlyResizing: false };
  private setFilterPanelCurrentlyResizing = (value: boolean): void => this.setState({ filterPanelCurrentlyResizing: value });
  private setPanelWidth = (value: number): void => this.setState({
    filterPanelWidth: clamp(value, ExplorerStyles.DefaultAdvancedFilterPanelWidth, ExplorerStyles.MaxAdvancedFilterPanelWidth)
  });

  public componentDidMount() {
    this.setToolbarState();
    // we want to know when we are in list or thumbnail view each time we change between them
    // or if we are in a new space/subspace
    this.sendShowExplorerEvent();
    const displayMode = this.getDisplayMode();
    Telemetry.sendEvent<VPCFeatureUsageEvent>({
      type: TelemetryEvents.VPCFeatureUsage,
      feature_name: vpcUsageTelemetryExplorerDisplayModeFeatureName,
      usage_type: 'impression',
      extra_parameter_a: this.props.spaceInfo.spaceType,
      extra_parameter_b: displayMode
    });
  }

  public componentDidUpdate(prevProps: ExplorerProps) {
    if (this.displayModeChanged(prevProps, this.props) || this.spaceChanged(prevProps, this.props)) {
      // we want to know when we are in list or thumbnail view each time we change between them
      // or if we are in a new space/subspace
      this.sendShowExplorerEvent();
    }

    if (this.toolbarStateChanged(prevProps, this.props)) {
      this.setToolbarState();
    }

    // Only announce completion when items are present — ExplorerEmptyState handles the zero-result case in the
    // same aria-live region, and the busy flag also resets on fetch failure, which would cause a false announcement.
    if (prevProps.isBusyFetchingItems && !this.props.isBusyFetchingItems && this.props.items.length > 0) {
      ScreenReaderService.instance.setScreenReaderMessage(Messages.contentLoadingCompleteMessage());
    }
  }

  private isConnectedToolbarEnabled() {
    return FeatureFlags.compactHeaderEnabled && !this.props.disableConnectedToolbar;
  }

  private setToolbarState() {
    if (this.isConnectedToolbarEnabled()) {
      this.props.setToolbarState({
        supportedDisplayModes: this.props.supportedDisplayModes,
        contentSort: this.props.disableSort ? null : this.props.contentSort,
        showDatasourceViewTypeDropDown: this.showDatasourceViewTypeDropDown(this.props),
        filtersApplied: this.hasFiltersApplied(this.props),
        hasTextSearchParameter: this.hasTextFilter(this.props),
        contentFilterControl: this.props.toolbarProps.toolbarContentFilterControl,
        // if the toolbar is disabled, show the filter button (but disabled) instead of hiding it
        showAdvancedFilterButton: this.props.toolbarProps.supportFilterPanel || (this.props.toolbarProps.toolbarVisibility === 'disabledButtonsOnRight'),
        disableButtonsOnRight: this.props.toolbarProps.toolbarVisibility === 'disabledButtonsOnRight',
        contentSpecificControls: this.props.toolbarProps.toolbarContentSpecificControls,
        hideSelection: this.props.toolbarProps.hideSelection,
        hideContentSpecificControlsInEmptyState: this.props.toolbarProps.hideContentSpecificControlsInEmptyState,
        changeViewMode: this.changeViewMode,
        datasourceViewTypeSelected: this.datasourceViewTypeSelected,
        advancedFilterButtonClickHandler: this.toggleAdvancedFilterPanel,
      });
    }
  }

  private toolbarStateChanged(prevProps: ExplorerProps, newProps: ExplorerProps): boolean {
    return !_.isEqual(prevProps.supportedDisplayModes, newProps.supportedDisplayModes)
      || prevProps.disableSort !== newProps.disableSort
      || this.showDatasourceViewTypeDropDown(prevProps) !== this.showDatasourceViewTypeDropDown(newProps)
      || this.hasFiltersApplied(prevProps) !== this.hasFiltersApplied(newProps)
      || this.hasTextFilter(prevProps) !== this.hasTextFilter(newProps)
      || !_.isEqual(prevProps.toolbarProps.toolbarContentFilterControl, newProps.toolbarProps.toolbarContentFilterControl)
      || prevProps.toolbarProps.supportFilterPanel !== newProps.toolbarProps.supportFilterPanel
      || prevProps.toolbarProps.toolbarVisibility !== newProps.toolbarProps.toolbarVisibility
      || !_.isEqual(prevProps.toolbarProps.toolbarContentSpecificControls, newProps.toolbarProps.toolbarContentSpecificControls)
      || prevProps.toolbarProps.hideSelection !== newProps.toolbarProps.hideSelection
      || prevProps.toolbarProps.hideContentSpecificControlsInEmptyState !== newProps.toolbarProps.hideContentSpecificControlsInEmptyState;
  }

  private spaceChanged(prevProps: ExplorerProps, newProps: ExplorerProps): boolean {
    return newProps.spaceInfo.spaceType !== prevProps.spaceInfo.spaceType
      || newProps.spaceInfo.subspaceName !== prevProps.spaceInfo.subspaceName
      || newProps.spaceInfo.item !== prevProps.spaceInfo.item
      || (newProps.spaceInfo.item && prevProps.spaceInfo.item
        && newProps.spaceInfo.item.id !== prevProps.spaceInfo.item.id);
  }

  private displayModeChanged(prevProps: ExplorerProps, newProps: ExplorerProps) {
    return newProps.explorerViewMode !== prevProps.explorerViewMode;
  }

  private sendShowExplorerEvent() {
    Telemetry.sendEvent<ShowExplorerEvent>(CreateExplorerEvent<ShowExplorerEvent>({
      type: TelemetryEvents.ShowExplorer
    }));
  }

  private renderToolbar() {
    if (this.props.toolbarProps.toolbarVisibility !== 'hidden') {
      if (this.isConnectedToolbarEnabled()) {
        return <ConnectedExplorerToolbar />;
      } else {
        return <ExplorerToolbar
          selectedItems={this.props.selectedItems}
          clearAllSelections={this.props.clearAllSelections}
          selectAll={this.props.selectAll}
          isSelectionBusy={this.props.isSelectionBusy}
          contentFilter={this.props.contentTypeFilterValues}
          advancedFilterButtonClickHandler={this.toggleAdvancedFilterPanel}
          filterPanelShown={this.props.showFilterPanel}
          currentViewMode={this.props.explorerViewMode}
          changeViewMode={this.changeViewMode}
          contentSort={this.props.disableSort ? null : this.props.contentSort}
          contentSortSelected={this.props.contentSortSelected}
          showDatasourceViewTypeDropDown={this.showDatasourceViewTypeDropDown(this.props)}
          datasourceViewTypeSelected={this.datasourceViewTypeSelected}
          hasTextSearchParameter={this.hasTextFilter(this.props)}
          filtersApplied={this.hasFiltersApplied(this.props)}
          itemCount={this.props.items.length}
          showAdvancedFilterButton={this.props.toolbarProps.supportFilterPanel || (this.props.toolbarProps.toolbarVisibility === 'disabledButtonsOnRight')}
          // if the toolbar is disabled, show the filter button (but disabled) instead of hiding it
          spaceInfo={this.props.spaceInfo}
          supportedDisplayModes={this.props.supportedDisplayModes}
          contentSpecificControls={this.props.toolbarProps.toolbarContentSpecificControls}
          contentFilterControl={this.props.toolbarProps.toolbarContentFilterControl}
          hideSelection={this.props.toolbarProps.hideSelection}
          disableButtonsOnRight={this.props.toolbarProps.toolbarVisibility === 'disabledButtonsOnRight'}
          hideContentSpecificControlsInEmptyState={this.props.toolbarProps.hideContentSpecificControlsInEmptyState}
        />;
      }
    }

    return null;
  }

  public render(): JSX.Element {
    const displayMode = this.getDisplayMode();
    const showFilterPanel = this.props.toolbarProps.supportFilterPanel && this.props.showFilterPanel;
    const { filterPanelWidth, filterPanelCurrentlyResizing } = this.state;
    const { contentAreaStyles, filterPanelStyles } = this.memoizedMakeResizerStyles(showFilterPanel, filterPanelWidth, filterPanelCurrentlyResizing);

    const ContentAreaClassNames = ExplorerStyles.ExplorerContentArea;
    const FilterPanelClassNames = ExplorerStyles.getAdvancedFilterPanelClassNames(showFilterPanel);

    return (
      <div className={ExplorerStyles.ExplorerContainer} data-tb-test-id={ExplorerTestIds.Explorer}>
        {this.renderToolbar()}
        {this.props.belowToolbarContent}

        <div className={this.getMainAreaClassNames(displayMode)}
          data-tb-test-id={ExplorerTestIds.ExplorerMainArea}>
          <div className={FilterPanelClassNames} data-tb-test-id={AdvancedFilterPanel.TestId} style={filterPanelStyles}>
            {this.renderAdvancedFilterPanel()}
          </div>

          <div className={ContentAreaClassNames} data-tb-test-id={ExplorerTestIds.ExplorerContentArea} style={contentAreaStyles}>
            {this.renderContentSection(displayMode)}
            {this.renderSpinner()}
            {this.renderNoContentSection()}
          </div>
        </div>
      </div>
    );
  }

  private memoizedMakeResizerStyles = defaultMemoize((showFilterPanel: boolean, filterPanelWidth: number, currentlyResizing: boolean): { contentAreaStyles: React.CSSProperties, filterPanelStyles: React.CSSProperties } => ({
    contentAreaStyles: { marginRight: showFilterPanel ? filterPanelWidth : 0, transition: currentlyResizing ? 'none' : undefined },
    filterPanelStyles: { width: filterPanelWidth, right: showFilterPanel ? 0 : -filterPanelWidth, pointerEvents: currentlyResizing ? 'none' : undefined }
  }));

  private getMainAreaClassNames(displayMode: DisplayMode) {
    const classNames = [ExplorerStyles.ExplorerMainArea];

    if (displayMode === 'thumbnail') {
      classNames.push(ExplorerStyles.ExplorerMainAreaForCardView);
    }

    return classNames.join(' ');
  }

  private hasFiltersApplied = (props: ExplorerProps): boolean => {
    const numberOfFilters = _.size(props.mixedContentFilterMap);
    const inSearchSpaceWithOnlySearchFilter = isSomeKindaSearchSpace(props.spaceInfo) && numberOfFilters === 1 && this.hasTextFilter(props);
    return numberOfFilters > 0 && !inSearchSpaceWithOnlySearchFilter;
  };

  private hasTextFilter = (props: ExplorerProps): boolean => {
    return !_.isUndefined(props.mixedContentFilterMap['search']);
  };

  private toggleAdvancedFilterPanel = () => {
    Telemetry.sendEvent<AdvancedFilterPanelToggleEvent>({
      type: TelemetryEvents.AdvancedFilterPanelToggle,
      openOrClose: this.props.showFilterPanel ? 'close' : 'open',
      mixedContentEnabled: true,
    });

    const onOpenFilterPanel = !this.props.showFilterPanel;
    this.props.updateFilterPanelVisibility(!this.props.showFilterPanel);

    if (onOpenFilterPanel) {
      //delay focus until after the enter-transtion animations are done
      const timeout = setTimeout(() => {
        const filterPanelElement = document.getElementById('filter-panel-container');
        const tabbables = tabbable(filterPanelElement);
        if (tabbables.length > 0) {
          tabbables[0].focus();
        }
      }, ExplorerStyles.TRANSITION_TIME * CONVERT_TO_MILLISECONDS);
      return () => clearTimeout(timeout);
    }
  };

  private changeViewMode = (viewMode: DisplayMode) => {
    this.props.changeExplorerViewMode(viewMode, true);
  };

  private requestSpaceContentsMissingSlice = (start: number, end: number) => {
    this.props.requestSpaceContentsMissingSlice(start, end);
  };

  private showDatasourceViewTypeDropDown = (props: ExplorerProps) => {
    const isShowingOnlyDatasourcesOnSpecificPages = (isProjectSpace(props.spaceInfo) || isSiteSpace(props.spaceInfo) || isSiteUserContentSubspace(props.spaceInfo))
      && props.contentTypeFilterValues.length === 1
      && (props.contentTypeFilterValues[0] === 'datasource'
        || props.contentTypeFilterValues[0] === 'dataconnection');

    return isShowingOnlyDatasourcesOnSpecificPages || IsWorkbookDatasourceSubspaceTab(this.props.spaceInfo);
  };

  private datasourceViewTypeSelected = (viewType: DatasourceViewTypeInfo): void => {
    // We filter to the specific type selected by user
    if (IsWorkbookDatasourceSubspaceTab(this.props.spaceInfo)) {
      const newSubspaceName = contentTypeToWorkbookSubspaceName(viewType.type) as WorkbookSubspaceName;
      if (newSubspaceName !== this.props.spaceInfo.subspaceName) {
        const url = newSubspaceName === 'datasources' ?
          this.props.urls.workbookDatasourcesUrl(this.props.spaceInfo.item.id) :
          getWorkbookUrl(this.props.urls, this.props.spaceInfo.item.id, 'dataconnections');

        Location.instance.setUrl(url);
      }

    } else if (isProjectSpace(this.props.spaceInfo) || isSiteSpace(this.props.spaceInfo) || isSiteUserContentSubspace(this.props.spaceInfo)) {
      this.props.filterSelected([viewType.type]);
    }
  };

  private renderContentSection(displayMode: DisplayMode) {
    let contentElement: React.ReactElement;
    const siteLuid = SessionSiteSelectors.siteLuid(getAppStore().getState());
    const ariaLabelledBy = document.getElementById(activeSubSpaceHeaderId) ? activeSubSpaceHeaderId : document.getElementById(spaceHeaderID) ? spaceHeaderID : '';
    if (displayMode === 'thumbnail') {
      contentElement = (
        <ConnectedCardView
          showActionMenu={!this.props.hideSelectionAndActionMenu}
          // disable checkbox-selection in recommendations page
          showSelection={!this.props.toolbarProps.hideSelection}
          reorderItemAction={this.props.reorderItemAction}
          allowDragAndDrop={this.props.allowDragAndDrop}
          reorderAccessibilityInstructions={this.props.reorderAccessibilityInstructions}
        />
      );
    } else if (displayMode === 'list') {
      // Determined if the current user is an admin for the server or site
      // default to false if we can't find SessionManagementService.
      const sessionManagementService = getSessionManagementServiceInstance();
      const isSessionSiteOrServerAdmin = sessionManagementService ?
        sessionManagementService.isSessionSiteOrServerAdmin() : false;
      const currentSort = this.props.disableSort ?
        null : this.props.contentSort;
      contentElement = (
        <ConnectedListView
          urls={this.props.urls}
          items={this.props.items}
          selectedItems={this.props.selectedItems}
          selectItem={this.props.selectItem}
          deselectItem={this.props.deselectItem}
          requestSpaceContentsMissingSlice={this.requestSpaceContentsMissingSlice}
          requestSpaceContentsNextPageWithToken={this.props.requestSpaceContentNextPageWithToken}
          contentNextPageToken={this.props.contentNextPageToken}
          contentFilter={this.props.contentTypeFilterValues}
          spaceInfo={this.props.spaceInfo}
          isBusyFetchingItems={this.props.isBusyFetchingItems}
          currentSort={currentSort}
          changeSort={this.props.addFieldToSortOrder}
          setSortOrder={this.props.contentSortSelected}
          isSessionSiteOrServerAdmin={isSessionSiteOrServerAdmin}
          isGuestEnabled={ServerSettingsUnauthenticated.isGuestEnabled()}
          moreItemsForTokenRequest={this.props.moreItemsForTokenRequest}
          ariaLabelledBy={ariaLabelledBy}
        />
      );
    }

    if (['thumbnail', 'list'].includes(displayMode)) {
      return (
        <ContentClickedTelemetryContext.Provider value={generateContentClickedTelemetryContext(this.props.spaceInfo, this.props.items)}>
          <FavoriteToggleClickedTelemetryContext.Provider value={generateFavoriteToggleClickedTelemetryContext(this.props.spaceInfo, this.props.items)}>
            <ActionMenuItemClickedTelemetryContext.Provider value={generateActionMenuItemClickedTelemetryContext(this.props.spaceInfo, this.props.items)}>
              <ExplorePageDisplayModeContext.Provider value={displayMode === 'thumbnail' ? ExploreDisplayMode.Grid : ExploreDisplayMode.List}>
                <CatalogProviders>
                  {contentElement}
                </CatalogProviders>
              </ExplorePageDisplayModeContext.Provider>
            </ActionMenuItemClickedTelemetryContext.Provider>
          </FavoriteToggleClickedTelemetryContext.Provider>
        </ContentClickedTelemetryContext.Provider>
      );
    }

    return <div>{Messages.errorUhOh()}</div>; // This should never happen
  }

  private renderAdvancedFilterPanel = () => {
    const props: AdvancedFilterPanelProps = {
      // the resize handle must be an immediate child of the scrollable div, so that the handle can occupy the full scrollable height
      children: <FilterPanelResizer setPanelWidth={this.setPanelWidth} setResizing={this.setFilterPanelCurrentlyResizing} />,
      mixedContentFilterMap: this.props.mixedContentFilterMap,
      spaceInfo: this.props.spaceInfo,
      shownContentTypes: this.props.contentTypeFilterValues,
    };
    if (isSiteUsersSpace(this.props.spaceInfo) || isGroupSpace(this.props.spaceInfo) || isGroupSetGroupsSubspace(this.props.spaceInfo)) {
      return <SiteUsersAdvancedFilterPanel {...props} />;
    } else if (isAllServerUsersSpace(this.props.spaceInfo)) {
      return <ConnectedServerUsersAdvancedFilterPanel {...props} />;
    }
    return <AdvancedFilterPanel {...props} />;
  };

  private getDisplayMode(): DisplayMode {
    const explorerDisplayMode = this.props.explorerViewMode;
    const supportedDisplayModes = this.props.supportedDisplayModes;

    if (supportedDisplayModes.includes(explorerDisplayMode)) {
      return explorerDisplayMode;
    } else if (supportedDisplayModes.length > 0) {
      return supportedDisplayModes[0];
    }

    return 'list';
  }

  private renderSpinner(): JSX.Element {
    this.props.isBusyFetchingItems && ScreenReaderService.instance.setScreenReaderMessage(Messages.contentLoadingMessage());
    return (
      this.props.isBusyFetchingItems &&
      <div className={ExplorerStyles.ListViewWaitingDiv}>
        <div style={{ paddingTop: 48 }}>
          <ActivitySpinnerWidget testId={ExplorerTestIds.ExplorerBusySpinner} />
        </div>
      </div>
    );
  }

  private clearMixedContentFilters = (): void => {
    this.props.clearMixedContentFilters();
    focusFilterSearchField();
  };

  private renderNoContentSection(): JSX.Element {
    if (!this.props.isBusyFetchingItems && this.props.items.length === 0) {
      // we display no filter empty state message first.
      // then we display any custom message from props
      // default custom message

      if (this.props.toolbarProps.supportFilterPanel && this.hasFiltersApplied(this.props)) {
        return (
          <ExplorerEmptyState
            dataTestId={ExplorerTestIds.ExplorerNoContentFilters}
            iconUrl={imgPath(FILTER_ICON_LARGE_PATH)}
            primaryMessage={Messages.contentEmptyFilterMessage()}
            secondaryMessage={''}
            actionButtonMessage={Messages.contentEmptyClearFilters()}
            onActionButton={this.clearMixedContentFilters}
          />
        );
      }

      if (this.props.emptyStateProps) {
        if (this.props.emptyStateProps.useContentTypeEmptyStateMessage === true && this.props.contentTypeFilterValues.length === 1) {
          return (
            <ExplorerEmptyState
              {...this.props.emptyStateProps}
              primaryMessage={getExplorableItemDefinition(this.props.contentTypeFilterValues[0]).emptyStateMessage()}
            />
          );
        }
        return <ExplorerEmptyState {...this.props.emptyStateProps} />;
      }

      if (this.props.contentTypeFilterValues.length === 1) {
        return (
          <ExplorerEmptyState
            dataTestId={ExplorerTestIds.ExplorerNoContent}
            iconUrl={''}
            primaryMessage={getExplorableItemDefinition(this.props.contentTypeFilterValues[0]).emptyStateMessage()}
            secondaryMessage={''}
          />
        );
      }

      return (
        <ExplorerEmptyState
          dataTestId={ExplorerTestIds.ExplorerNoContent}
          iconUrl={''}
          primaryMessage={Messages.contentEmptyMessage()}
          secondaryMessage={''}
        />
      );
    } else {
      return null;
    }
  }
}

export function generateContentClickedTelemetryContext(spaceInfo: SpaceInfo, allContents: ReadonlyArray<IExplorableItemTypeInfo>): ContentClickedTelemetryContextValueType {
  return function ContentClickedTelemetryContext(childFunction: ContentClickedChildFunction) {
    return (
      <ContentInfoContext.Consumer>
        {contentInfo =>
          <RelatedContentInfoContext.Consumer>
            {relatedContentInfo =>
              <ExplorePageDisplayModeContext.Consumer>
                {displayMode =>
                  <CardPositionContext.Consumer>
                    {contentPosition =>
                      <SearchTermDataContext.Consumer>
                        {searchTerm =>
                          childFunction(() => {
                            const eventHandler = generateTelemetryEventHandler(
                              TelemetryEvents.ContentClicked,
                              spaceInfo,
                              allContents,
                              contentInfo,
                              ContentLocation.Explore,
                              contentPosition,
                              {
                                displayMode: displayMode,
                                contentTotal: allContents.length,
                                searchTerm: searchTerm,
                                relatedContentId: relatedContentInfo && relatedContentInfo.relatedContentId,
                                // Known bug on TS prevents using the commented line and we have to use an if statemnte
                                // https://github.com/microsoft/TypeScript/issues/32455
                                // relatedContentType: relatedContentInfo && relatedContentInfo.relatedContentType,
                                relatedContentType: relatedContentInfo ? relatedContentInfo.relatedContentType : null
                              }
                            );
                            eventHandler();
                          })
                        }
                      </SearchTermDataContext.Consumer>
                    }
                  </CardPositionContext.Consumer>
                }
              </ExplorePageDisplayModeContext.Consumer>
            }
          </RelatedContentInfoContext.Consumer>
        }
      </ContentInfoContext.Consumer>
    );
  };
}

export function generateFavoriteToggleClickedTelemetryContext(spaceInfo: SpaceInfo, allContents: ReadonlyArray<IExplorableItemTypeInfo>) {
  return function FavoriteToggleClickedTelemetryContext(childFunction: FavoriteToggleClickedChildFunction) {
    return (
      <ContentInfoContext.Consumer>
        {contentInfo =>
          <ExplorePageDisplayModeContext.Consumer>
            {displayMode =>
              <CardPositionContext.Consumer>
                {contentPosition =>
                  <SearchTermDataContext.Consumer>
                    {searchTerm =>
                      childFunction((toggleType: FavoriteToggleType) => {
                        const eventHandler = generateTelemetryEventHandler(
                          TelemetryEvents.FavoriteToggleClicked,
                          spaceInfo,
                          allContents,
                          contentInfo,
                          ContentLocation.Explore,
                          contentPosition,
                          { displayMode: displayMode, contentTotal: allContents.length, searchTerm: searchTerm }
                        );
                        eventHandler(toggleType);
                      })
                    }
                  </SearchTermDataContext.Consumer>
                }
              </CardPositionContext.Consumer>
            }
          </ExplorePageDisplayModeContext.Consumer>
        }
      </ContentInfoContext.Consumer>
    );
  };
}

export function generateActionMenuItemClickedTelemetryContext(spaceInfo: SpaceInfo, allContents: ReadonlyArray<IExplorableItemTypeInfo>) {
  return function ActionMenuItemClickedTelemetryContext(childFunction: ActionMenuItemClickedChildFunction) {
    return (
      <ContentInfoContext.Consumer>
        {contentInfo =>
          <ExplorePageDisplayModeContext.Consumer>
            {displayMode =>
              <CardPositionContext.Consumer>
                {contentPosition =>
                  <SearchTermDataContext.Consumer>
                    {searchTerm =>
                      childFunction((actionType: string) => {
                        const eventHandler = generateTelemetryEventHandler(
                          TelemetryEvents.ActionMenuItemClicked,
                          spaceInfo,
                          allContents,
                          contentInfo,
                          ContentLocation.Explore,
                          contentPosition,
                          { displayMode: displayMode, contentTotal: allContents.length, searchTerm: searchTerm }
                        );
                        eventHandler(actionType);
                      })
                    }
                  </SearchTermDataContext.Consumer>
                }
              </CardPositionContext.Consumer>
            }
          </ExplorePageDisplayModeContext.Consumer>
        }
      </ContentInfoContext.Consumer>
    );
  };
}


